import { GridActionsCellItem } from '@mui/x-data-grid/components';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { Delete, Visibility } from '@mui/icons-material';
import { Tooltip, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { ConsignmentResponse } from '@/api/bol';
import {
  formatDateTime,
  getEntityDisplayName,
  formatExternalReferences,
  formatValueWithUnit,
  renderBooleanChip,
} from '@/utils/datagridFormatters';

interface ConsignmentColumnsProps {
  onDelete: (consignment: ConsignmentResponse) => void;
  canDelete?: boolean;
  deleteTooltip?: string;
}

export const createConsignmentColumns = ({ onDelete, canDelete = true, deleteTooltip = '' }: ConsignmentColumnsProps): GridColDef[] => [
  {
    field: 'id',
    headerName: 'ID',
    flex: 1.6,
    minWidth: 220,
    valueGetter: (_value: unknown, row: { id?: string }) => row.id || '-',
  },
  {
    field: 'consignor',
    headerName: 'Consignor',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (_value: unknown, row: { consignor?: { commercialName?: string | null; legalEntityName?: string | null; name?: string | null } }) =>
      getEntityDisplayName(row.consignor),
  },
  {
    field: 'consignee',
    headerName: 'Consignee',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (_value: unknown, row: { consignee?: { commercialName?: string | null; legalEntityName?: string | null; name?: string | null } }) =>
      getEntityDisplayName(row.consignee),
  },
  {
    field: 'originLocation',
    headerName: 'Origin',
    flex: 1.1,
    minWidth: 150,
    valueGetter: (_value: unknown, row: { originLocation?: { address?: { addressLocality?: string | null; postalCode?: string | null } | null } }) => {
      const locality = row.originLocation?.address?.addressLocality;
      const postalCode = row.originLocation?.address?.postalCode;
      return [locality, postalCode].filter(Boolean).join(', ') || '-';
    },
  },
  {
    field: 'finalLocation',
    headerName: 'Destination',
    flex: 1.1,
    minWidth: 150,
    valueGetter: (_value: unknown, row: { finalLocation?: { address?: { addressLocality?: string | null; postalCode?: string | null } | null } }) => {
      const locality = row.finalLocation?.address?.addressLocality;
      const postalCode = row.finalLocation?.address?.postalCode;
      return [locality, postalCode].filter(Boolean).join(', ') || '-';
    },
  },
  {
    field: 'requestedStart',
    headerName: 'Requested Start',
    flex: 1.1,
    minWidth: 160,
    valueGetter: (_value: unknown, row: { requestedStartTimeRange?: { earliestDateTime?: string } }) =>
      formatDateTime(row.requestedStartTimeRange?.earliestDateTime),
  },
  {
    field: 'requestedEnd',
    headerName: 'Requested End',
    flex: 1.1,
    minWidth: 160,
    valueGetter: (_value: unknown, row: { requestedEndTimeRange?: { latestDateTime?: string } }) =>
      formatDateTime(row.requestedEndTimeRange?.latestDateTime),
  },
  {
    field: 'weight',
    headerName: 'Weight',
    flex: 0.9,
    minWidth: 120,
    valueGetter: (_value: unknown, row: { weight?: { value?: number | null; unit?: string | null } }) =>
      formatValueWithUnit(row.weight?.value, row.weight?.unit),
  },
  {
    field: 'volume',
    headerName: 'Volume',
    flex: 0.9,
    minWidth: 120,
    valueGetter: (_value: unknown, row: { volume?: { value?: number | null; unit?: string | null } }) =>
      formatValueWithUnit(row.volume?.value, row.volume?.unit),
  },
  {
    field: 'consignmentDetailsCount',
    headerName: 'Details',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (_value: unknown, row: { consignmentDetails?: unknown[] }) => row.consignmentDetails?.length || 0,
  },
  {
    field: 'servicesCount',
    headerName: 'Services',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (_value: unknown, row: { services?: unknown[] }) => row.services?.length || 0,
  },
  {
    field: 'serviceRequestsCount',
    headerName: 'Service Requests',
    flex: 1,
    minWidth: 150,
    valueGetter: (_value: unknown, row: { serviceRequests?: unknown[] }) => row.serviceRequests?.length || 0,
  },
  {
    field: 'externalReferences',
    headerName: 'External References',
    flex: 1.5,
    minWidth: 220,
    valueGetter: (_value: unknown, row: { externalReferences?: Array<{ externalReferenceType?: string | null; reference?: string | null }> }) =>
      formatExternalReferences(row.externalReferences),
  },
  {
    field: 'hasSpecialInstructions',
    headerName: 'Has Instructions',
    flex: 0.9,
    minWidth: 130,
    renderCell: (params) => renderBooleanChip((params.row.specialInstructions?.length || 0) > 0),
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: 120,
    getActions: (params) => [
      <Tooltip key="view" title="View details">
        <IconButton
          component={Link}
          to={`/consignments/${params.row.id as string}`}
          size="small"
          color="primary"
          aria-label="View Details"
        >
          <Visibility fontSize="small" />
        </IconButton>
      </Tooltip>,
      <GridActionsCellItem
        key="delete"
        icon={
          <Tooltip title={!canDelete ? deleteTooltip : 'Delete'}>
            <span>
              <Delete color={!canDelete ? 'disabled' : 'error'} />
            </span>
          </Tooltip>
        }
        label="Delete"
        onClick={() => onDelete(params.row as ConsignmentResponse)}
        disabled={!canDelete}
        showInMenu={false}
      />,
    ],
  },
];

export const defaultConsignmentColumnVisibilityModel = {
  id: true,
  consignor: true,
  consignee: true,
  originLocation: true,
  finalLocation: true,
  requestedStart: true,
  requestedEnd: true,
  weight: true,
  volume: true,
  consignmentDetailsCount: true,
  servicesCount: false,
  serviceRequestsCount: false,
  externalReferences: false,
  hasSpecialInstructions: false,
};
